import React, { useEffect, useState } from 'react';
import api from '../api/client';
import PermissionsGrid from './PermissionsGrid';

// Персональные исключения поверх роли. Чекбокс показывает эффективное право,
// подсветка — отличие от роли. Клик по ячейке со значением роли создаёт
// исключение; возврат к значению роли убирает его (наследование).
function PersonalAccessEditor({ employeeID, pages, onSaved }) {
    const [data, setData] = useState(null); // { role, rolePages }
    const [ov, setOv] = useState({});       // pageKey -> { view?, edit?, delete? }; отсутствие = наследовать
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/api/admin/users/${employeeID}/permissions`)
            .then(r => {
                const map = {};
                for (const row of r.data.overrides) {
                    const page = {};
                    if (row.can_view !== null) page.view = !!row.can_view;
                    if (row.can_edit !== null) page.edit = !!row.can_edit;
                    if (row.can_delete !== null) page.delete = !!row.can_delete;
                    if (Object.keys(page).length) map[row.page_key] = page;
                }
                setOv(map);
                setData({ role: r.data.role, rolePages: r.data.rolePages });
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to load permissions'));
    }, [employeeID]);

    if (error) return <div className="admin-panel__error">{error}</div>;
    if (!data) return <div className="admin-panel__muted">Loading…</div>;

    const roleValue = (pageKey, action) => !!data.rolePages[pageKey]?.[action];

    const getCell = (pageKey, action) => {
        const o = ov[pageKey] ? ov[pageKey][action] : undefined;
        return { checked: o === undefined ? roleValue(pageKey, action) : o, marked: o !== undefined };
    };

    const toggle = (pageKey, action) => {
        setOv(prev => {
            const page = { ...(prev[pageKey] || {}) };
            const current = page[action] !== undefined ? page[action] : roleValue(pageKey, action);
            const wanted = !current;
            if (wanted === roleValue(pageKey, action)) delete page[action];
            else page[action] = wanted;
            const next = { ...prev };
            if (Object.keys(page).length === 0) delete next[pageKey];
            else next[pageKey] = page;
            return next;
        });
    };

    const save = () => {
        setSaving(true);
        setError(null);
        const overrides = Object.entries(ov).map(([page_key, p]) => ({
            page_key,
            view: p.view === undefined ? null : p.view,
            edit: p.edit === undefined ? null : p.edit,
            delete: p.delete === undefined ? null : p.delete,
        }));
        api.put(`/api/admin/users/${employeeID}/permissions`, { overrides })
            .then(() => { if (onSaved) onSaved(); })
            .catch(err => setError(err.response?.data?.error || 'Failed to save'))
            .finally(() => setSaving(false));
    };

    const diffCount = Object.keys(ov).length;

    return (
        <div className="personal-access">
            <div className="personal-access__bar">
                <span>
                    Role: <strong>{data.role ? data.role.title : '—'}</strong>
                    {diffCount > 0 && <span className="admin-panel__muted"> · {diffCount} personal exception(s)</span>}
                </span>
                <span className="personal-access__bar-btns">
                    <button type="button" className="admin-panel__grant-btn" disabled={saving || diffCount === 0}
                        onClick={() => setOv({})}>
                        Reset to role
                    </button>
                    <button type="button" className="admin-panel__save-btn" disabled={saving} onClick={save}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </span>
            </div>
            <PermissionsGrid pages={pages} getCell={getCell} onToggle={toggle} />
        </div>
    );
}

export default PersonalAccessEditor;
